import { BooleanInput } from '@angular/cdk/coercion';
import { booleanAttribute, Component, computed, effect, input, linkedSignal, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideChevronDown, lucideSearch } from '@ng-icons/lucide';
import { TrierParPipe } from '@shared/pipes/trier-par.pipe';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';

@Component({
  selector: 'app-chip-filtre',
  imports: [HlmIconImports, HlmDropdownMenuImports, TrierParPipe, HlmInputGroupImports, FormsModule],
  templateUrl: './chip-filtre.component.html',
  providers: [provideIcons({ lucideChevronDown, lucideSearch })],
})
export class ChipFiltreComponent<T extends { key: string; value: string }> {
  readonly items = input.required<T[]>();
  readonly selectedItems = input.required<string[] | string>();
  readonly sortKey = input<string | null>('key');

  readonly showSearchbar = input<boolean, BooleanInput>(false, { transform: booleanAttribute });
  readonly multiple = input<boolean, BooleanInput>(false, { transform: booleanAttribute });

  readonly type = input.required<string>();

  readonly onChange = output<string[] | string | undefined>();

  readonly label = computed(() => {
    let label = '';
    if (this.multiple()) {
      const selectedItems = this.selectedItems() as string[];
      label = selectedItems.map((x) => this.items().filter((y) => y.key === x)[0]?.value).join(', ');
    } else {
      label = this.items().find((x) => x.key === (this.selectedItems() as string))?.value ?? '';
    }

    return `${this.type()} : ${label}`;
  });

  readonly filteredItems = linkedSignal(() => this.items());
  readonly search = model<string>();

  readonly areAllItemsSelected = computed(() => this.selectedItems().length === this.items().length);

  isChecked = (key: string) => this.selectedItems().includes(key);

  constructor() {
    effect(() => {
      const search = this.search();

      this.filteredItems.set(this.items().filter((x) => x.value.includes(search ?? '')));
    });
  }

  toggleAll() {
    if (this.areAllItemsSelected()) {
      this.onChange.emit([] as string[]);
    } else {
      this.onChange.emit([...this.items().map((x) => x.key)]);
    }
  }

  onSelect(key: string) {
    if (this.multiple()) {
      const selectedItems = this.selectedItems() as string[];
      const index = selectedItems.findIndex((x) => x === key);
      if (index === -1) {
        selectedItems.push(key);
      } else {
        selectedItems.splice(index, 1);
      }
      this.onChange.emit(selectedItems);
    } else {
      const selectedItem = this.selectedItems() as string;
      this.onChange.emit(key === selectedItem ? undefined : key);
    }
  }
}
